import { useQuery, useQueries, useMutation, useQueryClient } from '@tanstack/react-query';
import { format, addDays } from 'date-fns';
import {
  fetchArrivals,
  fetchDepartures,
  fetchInHouse,
  searchReservations,
  updateBookingPaymentType,
  type FrontDeskDateQuery,
  type InHouseQuery,
  type SearchReservationsQuery,
  type BookingEntry,
  type PaymentType,
} from '@/services/api/frontdesk.api';

export function useArrivals(query: FrontDeskDateQuery) {
  return useQuery({
    queryKey: ['frontdesk', 'arrivals', query],
    queryFn: () => fetchArrivals(query),
    enabled: !!query.propertyId,
    staleTime: 30_000,
  });
}

export function useDepartures(query: FrontDeskDateQuery) {
  return useQuery({
    queryKey: ['frontdesk', 'departures', query],
    queryFn: () => fetchDepartures(query),
    enabled: !!query.propertyId,
    staleTime: 30_000,
  });
}

export function useInHouse(query: InHouseQuery) {
  return useQuery({
    queryKey: ['frontdesk', 'in-house', query],
    queryFn: () => fetchInHouse(query),
    enabled: !!query.propertyId,
    staleTime: 30_000,
  });
}

export function useSearchReservations(query: SearchReservationsQuery, enabled: boolean) {
  return useQuery({
    queryKey: ['frontdesk', 'search', query],
    queryFn: () => searchReservations(query),
    staleTime: 30_000,
    enabled,
  });
}

/**
 * Set (or clear) a booking's payment type. Invalidates the front-desk,
 * reservations and bookings caches so every surface (arrivals, reservation
 * detail, bills/folio) re-reads the new value.
 */
export function useUpdateBookingPaymentType() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, paymentType }: { id: string; paymentType: PaymentType | null }) =>
      updateBookingPaymentType(id, paymentType),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['frontdesk'] });
      queryClient.invalidateQueries({ queryKey: ['reservations'] });
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
      queryClient.invalidateQueries({ queryKey: ['folio'] });
    },
  });
}

/** Grouped result for a single day */
export interface DayGroup {
  date: string;
  entries: BookingEntry[];
}

/**
 * Fetch arrivals for the next N days (default 7), one request per day in parallel.
 * Returns an array of { date, entries } grouped by date.
 */
export function useArrivalsMultiDay(propertyId: string | undefined, days = 7) {
  const today = new Date();
  const dates = Array.from({ length: days }, (_, i) =>
    format(addDays(today, i), 'yyyy-MM-dd'),
  );

  const queries = useQueries({
    queries: dates.map((date) => ({
      queryKey: ['frontdesk', 'arrivals', { date, propertyId }],
      queryFn: () => fetchArrivals({ date, propertyId: propertyId! }),
      enabled: !!propertyId,
      staleTime: 30_000,
    })),
  });

  const isLoading = queries.some((q) => q.isLoading);
  const data: DayGroup[] = dates.map((date, i) => ({
    date,
    entries: (queries[i].data as { data?: BookingEntry[] } | undefined)?.data ?? (queries[i].data as BookingEntry[] | undefined) ?? [],
  }));

  return { data, isLoading };
}

/**
 * Fetch departures for the next N days (default 7), one request per day in parallel.
 * Returns an array of { date, entries } grouped by date.
 */
export function useDeparturesMultiDay(propertyId: string | undefined, days = 7) {
  const today = new Date();
  const dates = Array.from({ length: days }, (_, i) =>
    format(addDays(today, i), 'yyyy-MM-dd'),
  );

  const queries = useQueries({
    queries: dates.map((date) => ({
      queryKey: ['frontdesk', 'departures', { date, propertyId }],
      queryFn: () => fetchDepartures({ date, propertyId: propertyId! }),
      enabled: !!propertyId,
      staleTime: 30_000,
    })),
  });

  const isLoading = queries.some((q) => q.isLoading);
  const data: DayGroup[] = dates.map((date, i) => ({
    date,
    entries: (queries[i].data as { data?: BookingEntry[] } | undefined)?.data ?? (queries[i].data as BookingEntry[] | undefined) ?? [],
  }));

  return { data, isLoading };
}
